import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, ExternalLink, LayoutDashboard } from 'lucide-react'
import { BuilderTopHeader } from './BuilderNavTabs'
import { FeatureUnavailable } from './FeatureUnavailable'

// List served by Elasticsearch (grants-filtered natively, auto-indexed). Hits are
// flat under `contents` (render_mode: decoupled), so name/slug/id are top-level.
const COLLECTION_URL = '/api/elasticsearch/dashboards/config/dashboards_list'
const esItems = (payload) => Array.isArray(payload?.contents) ? payload.contents.filter((d) => d.id) : []
const SAVE_URL = '/decoupled-api/save?display=decoupled&entity_type=node&bundle=dashboard'

/**
 * Onglet « Tableaux de bord » du Page Builder : liste les dashboards gérés et
 * ouvre chacun dans l'éditeur par son slug (`/builder/dashboards/<slug>`).
 * Miroir de BuilderHome (pages), câblé sur la collection décowmoise `dashboard`.
 *
 * Pourquoi un composant React et non du YAML reactive-json : cette liste vit
 * DANS le chrome du builder (`BuilderTopHeader` + `BuilderNavTabs`, thème teal),
 * une surface d'administration que reactive-json n'authore pas ; le hub public
 * `/dashboards` (lui, en YAML) reste la voie déclarative côté application.
 */
export const BuilderDashboardsHome = ({ embedded = false }) => {
  // Sous le menu orbit (`embedded`, routes `withMenu`) : remplir le `main`
  // d'OrbitChrome plutôt qu'imposer 100vh, sinon la page dépasse d'une hauteur
  // de barre de menu. Standalone : plein écran comme avant.
  const shellClass = `${embedded ? 'flex-1' : 'min-h-screen'} bg-gray-50`

  const [dashboards, setDashboards] = useState([])
  const [loading, setLoading] = useState(true)
  const [available, setAvailable] = useState(true)
  const [creating, setCreating] = useState(false)
  const navigate = useNavigate()

  // Résilience : un endpoint indisponible (bundle `dashboard` absent = module
  // orbit_dashboard désactivé) marque la fonctionnalité comme non active, au lieu
  // d'un état vide trompeur (« créez-en un » alors qu'on ne peut pas).
  const load = useCallback(async () => {
    try {
      const res = await fetch(COLLECTION_URL, { credentials: 'include', cache: 'no-store' })
      if (!res.ok) { setAvailable(false); return }
      setDashboards(esItems(await res.json()))
    } catch {
      setAvailable(false)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const openDashboard = (d) => navigate(`/builder/dashboards/${d.slug || d.id}?edit=1`)

  // Crée un tableau vide puis ouvre son éditeur (le slug est récupéré au refetch,
  // l'écho de save n'exposant pas de nouvel id fiable).
  const createDashboard = async () => {
    setCreating(true)
    try {
      const token = await (await fetch('/session/token', { credentials: 'include' })).text()
      await fetch(SAVE_URL, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
        body: JSON.stringify({ data: { name: 'Nouveau tableau de bord', layout: '[]' } }),
      })
      const res = await fetch(COLLECTION_URL, { credentials: 'include', cache: 'no-store' })
      const newest = esItems(res.ok ? await res.json() : []).sort((a, b) => Number(b.id) - Number(a.id))[0]
      if (newest) navigate(`/builder/dashboards/${newest.slug || newest.id}?edit=1`)
      else await load()
    } finally {
      setCreating(false)
    }
  }

  if (loading) {
    return (
      <div className={`${shellClass} flex items-center justify-center`} role="status" aria-live="polite">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-teal-700" />
        <span className="sr-only">Chargement des tableaux de bord…</span>
      </div>
    )
  }

  if (!available) {
    return (
      <div className={shellClass}>
        <BuilderTopHeader />
        <main className="max-w-5xl mx-auto px-6 py-10">
          <FeatureUnavailable
            title="Tableaux de bord non disponibles"
            detail="La fonctionnalité Tableaux de bord n'est pas active sur ce serveur (module désactivé)."
          />
        </main>
      </div>
    )
  }

  return (
    <div className={shellClass}>
      <BuilderTopHeader
        rightSlot={
          dashboards.length > 0 && (
            <button
              onClick={createDashboard}
              disabled={creating}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-teal-700 text-white hover:bg-teal-800 transition-colors disabled:opacity-50"
            >
              <Plus size={14} />
              Nouveau tableau
            </button>
          )
        }
      />

      <main className="max-w-5xl mx-auto px-6 py-10">
        {dashboards.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-16 h-16 bg-teal-50 rounded-2xl flex items-center justify-center mb-6">
              <LayoutDashboard size={28} className="text-teal-600" />
            </div>
            <h1 className="text-2xl font-semibold text-gray-800 mb-2">Tableaux de bord</h1>
            <p className="text-sm text-gray-400 max-w-sm mb-8">
              Créez votre premier tableau de bord et composez-le, bloc par bloc.
            </p>
            <button
              onClick={createDashboard}
              disabled={creating}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium bg-teal-700 text-white hover:bg-teal-800 transition-colors shadow-sm disabled:opacity-50"
            >
              <Plus size={16} />
              Créer mon premier tableau
            </button>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-lg font-semibold text-gray-800">Mes tableaux de bord</h1>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {dashboards.map(d => (
                <div
                  key={d.id}
                  role="button"
                  tabIndex={0}
                  aria-label={`Éditer ${d.name || d.slug}`}
                  className="bg-white rounded-xl border border-gray-200 p-5 flex flex-col gap-3 hover:border-teal-300 hover:shadow-sm transition-all cursor-pointer group focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-600"
                  onClick={() => openDashboard(d)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDashboard(d) } }}
                >
                  <div className="w-full h-24 bg-gray-50 rounded-lg flex items-center justify-center border border-gray-100">
                    <LayoutDashboard size={20} className="text-gray-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 group-hover:text-teal-700 truncate transition-colors">
                      {d.name || d.slug}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5 truncate font-mono">/dashboards/{d.slug || d.id}</p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); openDashboard(d) }}
                    className="flex items-center gap-1.5 text-xs font-medium text-teal-700 hover:text-teal-900 transition-colors"
                  >
                    <ExternalLink size={12} />
                    Éditer
                  </button>
                </div>
              ))}
              <button
                onClick={createDashboard}
                disabled={creating}
                className="flex flex-col items-center justify-center gap-2 p-6 rounded-xl border-2 border-dashed border-gray-200 text-gray-400 hover:border-teal-400 hover:text-teal-600 hover:bg-teal-50 transition-all min-h-[120px] disabled:opacity-50"
              >
                <Plus size={22} />
                <span className="text-sm font-medium">Nouveau tableau</span>
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}
